import styles from "./multipleAgenda.module.scss";
import { AppContext, AppAction } from "helpers/context";
import { MapContext } from "helpers/map";
import { useContext, useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  getLocalScaleLocations,
  pctRaw,
  safePctRaw,
  formatKM,
  handleFilterLocations
} from "helpers/helpers";
import { ReactComponent as MyLocationSvg } from "assets/my_location.svg";
import { ReactComponent as MapLegendPinSvg } from "assets/icon_map_legend_pin.svg";
import {
  ToggleButtonGroup,
  ToggleButton,
  Divider,
  Tooltip
} from "@mui/material";
import * as turf from "@turf/turf";

function MultipleAgenda() {
  const [context, dispatch] = useContext(AppContext);
  const { AMap, map } = useContext(MapContext);
  const [t] = useTranslation();

  const scaleTypeRef = useRef(context.scaleType);

  const [viewportVersion, setViewportVersion] = useState(0);
  const [checkedCounts, setCheckedCounts] = useState(0);
  const [legendArr, setLegendArr] = useState<any[]>([]);
  const unit =
    context.currentMultipleLocations?.aggregationUnit ?? "numberOfLocations";
  const isNumberOfLocations = unit === "numberOfLocations";

  const getLegendArrData = (total: number, start = 1) => {
    const color = [
      "#fff8a1",
      "#ffcb8a",
      "#ff9c86",
      "#ee6c91",
      "#cb419c",
      "#9c2499",
      "#61168e",
      "#001789"
    ];

    const base = Math.pow(total / start, 1 / 8);

    let current = start;
    const thresholds: any[] = [{ color: color[0], value: current }];

    for (let i = 1; i < color.length; i++) {
      current = current * base;
      thresholds.push({ color: color[i], value: current });
      thresholds[i - 1].endValue = current;
    }
    thresholds[7].endValue = total;
    return thresholds;
  };

  const handleClickMyLocation = () => {
    if (
      context.currentMultipleLocations &&
      context.currentMultipleLocations.items
    ) {
      let bounds: any = [];
      context.currentMultipleLocations.items.forEach((item: any) => {
        if (item.checked) {
          bounds.push([item.longitude, item.latitude]);
        }
      });
      if (bounds.length === 0) return;
      let bbox: any = turf.bboxPolygon(turf.bbox(turf.multiPoint(bounds)));
      var myBounds = new AMap.Bounds(
        [bbox.bbox[2], bbox.bbox[3]],
        [bbox.bbox[0], bbox.bbox[1]]
      );
      map?.setBounds(myBounds, true, [20, 20, 20, 20]);
    }
  };

  useEffect(() => {
    // 1) checked 数量
    let data = handleFilterLocations({
      data: context.currentMultipleLocations?.items || [],
      unit,
      multipleLocationsSearch: context.multipleLocationsSearch,
      locationsFilterInfo: context.locationsFilterInfo,
      filterChecked: true
    });
    setCheckedCounts(data.length);

    let arr: any[] = [];

    if (isNumberOfLocations) {
      // 使用 itemCount 计算
      const total = context.currentMultipleLocations?.itemCount ?? 0;
      arr = getLegendArrData(total);
    } else {
      // 使用 items 中 unit 字段计算，total + min 通过 Math.round 累计
      const items = context.currentMultipleLocations?.items ?? [];
      const { total, min } = items.reduce(
        (acc: any, item: any) => {
          const num = item[unit];
          if (!Number.isFinite(num)) return acc;
          const val = Math.round(num); // 保证数值按整数累计
          if (!val) return acc;
          acc.total += val;
          acc.min = acc.min === null ? val : Math.min(acc.min, val);
          return acc;
        },
        { total: 0, min: null as number | null }
      );

      const safeMin = min === null ? 0 : min;
      if (total && safeMin) arr = getLegendArrData(total, safeMin);
    }

    setLegendArr(arr.reverse());
  }, [
    context.currentMultipleLocations?.items,
    unit,
    context.multipleLocationsSearch,
    context.locationsFilterInfo
  ]);

  useEffect(() => {
    scaleTypeRef.current = context.scaleType;
  }, [context.scaleType]);

  const handleChangeScaleType = useCallback(() => {
    if (!map) return;
    const zoom = map.getZoom();
    if (zoom == null) return;

    const current = scaleTypeRef.current;

    // < 11.63 éšè—
    if (zoom < 11.63) {
      if (current !== undefined) {
        // 仅在变化时更新，避免重复 dispatch
        dispatch({ type: AppAction.setScaleType, payload: undefined });
      }
    } else {
      setViewportVersion((v) => v + 1);
      if (!current) {
        // >= 11.63 时 current 为空则设置为 local
        dispatch({ type: AppAction.setScaleType, payload: "local" });
      }
    }
  }, [map, dispatch]);

  useEffect(() => {
    if (!map) return;
    map.on("moveend", handleChangeScaleType);
    map.on("resize", handleChangeScaleType);

    // 初始化后再触发一次，确保状态与 zoom 同步
    handleChangeScaleType();

    return () => {
      map.off("moveend", handleChangeScaleType);
      map.off("resize", handleChangeScaleType);
    };
  }, [map, handleChangeScaleType]);

  const legendDom = () => {
    const current = context.currentMultipleLocations;
    if (!current) return null;
    const itemCount = current.itemCount;
    const sourceItems = current.items ?? [];

    // 当维度不是 numberOfLocations 时，使用可选聚合数据
    const useLocalVisible =
      context.scaleType === "local" && !isNumberOfLocations;
    let items = sourceItems;
    if (useLocalVisible && map) {
      items = getLocalScaleLocations(items, map);
    }

    let total = 0;
    let min = Infinity;
    let max = -Infinity;

    if (!isNumberOfLocations) {
      items.forEach((it: any) => {
        if (it.checked) {
          const v = it[unit];
          if (v && Math.round(v)) {
            let num = Math.round(v);
            total += num;
            if (num < min) min = num;
            if (num > max) max = num;
          }
        }
      });
    }

    let shouldShowLegend = false;

    // 阈值为 1 的情况单独处理
    if (isNumberOfLocations) {
      if (itemCount && itemCount > 9) {
        shouldShowLegend = true;
      }
    } else {
      const hasValidMin = min !== Infinity && max !== -Infinity;
      const range = (useLocalVisible ? max : total) - min;
      const rangeIsEnough = range > 9;
      if (hasValidMin && rangeIsEnough) {
        shouldShowLegend = true;
      }
    }

    if (shouldShowLegend) {
      // numberOfLocations 场景优先沿用 state 中的 legendArr
      const legendArrToUse = (() => {
        if (!isNumberOfLocations && useLocalVisible) {
          return getLegendArrData(max, min).reverse();
        }
        return legendArr; // 直接返回已计算结果
      })();

      const totalForPercent = legendArrToUse?.[0]?.endValue ?? 0;
      return (
        <>
          {legendArrToUse.map((item, index) => {
            // 显示百分比时做边界保护
            const fromLabel = formatKM(item.value.toFixed());
            const toLabel = formatKM(item.endValue.toFixed());
            // 计算百分比并保证结果在安全范围
            const percentText =
              index === 0
                ? `(> ${pctRaw(item.value, item.endValue)}%)`
                : `(${safePctRaw(item.value, totalForPercent)} - ${safePctRaw(
                    item.endValue,
                    totalForPercent
                  )}%)`;
            return (
              <div key={index} className={styles.legends}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    height: "21px"
                  }}
                >
                  <MapLegendPinSvg
                    className="w-[12px] h-[16px] mb-[3px] mr-[8px]"
                    style={{ fill: item.color }}
                  />
                  <div className="w-[200px] whitespace-nowrap">
                    <span>{fromLabel}</span>
                    <span> - </span>
                    <span>{toLabel}</span>
                    <span>{percentText}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      );
    }
    if (!isNumberOfLocations && useLocalVisible && max === -Infinity)
      return null;
    // 不显示 Legend 时，按 total 渲染兜底值
    return (
      <div className={styles.legend}>
        <div
          style={{ display: "flex", alignItems: "flex-end", height: "21px" }}
        >
          <MapLegendPinSvg className="w-[12px] h-[16px] mb-[3px] fill-[#001789]" />
          <span style={{ whiteSpace: "nowrap" }}>
            100%(
            {isNumberOfLocations
              ? itemCount
              : formatKM(useLocalVisible ? max : total)}
            )
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      {context.currentMultipleLocations && (
        <div className={styles.multipleAgenda}>
          <div className={styles.header}>
            <div className={styles.text}>
              {context.currentMultipleLocations.name}
            </div>
            <MyLocationSvg
              id="loc"
              className="w-[16px] h-[16px] fill-[#0009] cursor-pointer"
              onClick={handleClickMyLocation}
            />
          </div>

          <div>
            <div className={styles.subtitle}>
              <span>
                {t(
                  "locations." +
                    context.currentMultipleLocations.aggregationUnit
                )}
              </span>
              <span>
                ({t("legend.showingLocations")} {checkedCounts}/
                {context.currentMultipleLocations.itemCount})
              </span>
            </div>

            {context.scaleType && (
              <div>
                <Divider />
                <ToggleButtonGroup
                  value={context.scaleType}
                  onChange={(event, value) =>
                    value &&
                    dispatch({ type: AppAction.setScaleType, payload: value })
                  }
                  className={styles.subToggle}
                  exclusive
                  color="success"
                  size="small"
                  fullWidth
                >
                  <Tooltip
                    title={
                      <>
                        <div>
                          {t("tooltip.globalScale", {
                            key: t("locations." + unit)
                          })}
                        </div>
                        <div>
                          {t("tooltip.localScale", {
                            key: t("locations." + unit)
                          })}
                        </div>
                      </>
                    }
                    sx={{ maxWidth: "400px" }}
                    placement="top"
                  >
                    <ToggleButton value="global">
                      {t("buttons.global")}
                    </ToggleButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      <>
                        <div>
                          {t("tooltip.globalScale", {
                            key: t("locations." + unit)
                          })}
                        </div>
                        <div>
                          {t("tooltip.localScale", {
                            key: t("locations." + unit)
                          })}
                        </div>
                      </>
                    }
                    sx={{ maxWidth: "400px" }}
                    placement="top"
                  >
                    <ToggleButton value="local">
                      {t("buttons.local")}
                    </ToggleButton>
                  </Tooltip>
                </ToggleButtonGroup>
              </div>
            )}
            {legendDom()}
          </div>
        </div>
      )}
    </>
  );
}

export default MultipleAgenda;


